<template>
	<div>
		<div
			:id="id"
			class="relative w-full h-full"
			:style="{
				aspectRatio: `${viewboxW} / ${viewboxH}`,
				maxWidth: `${viewboxW}px`,
				maxHeight: `${viewboxH}px`
			}"
		>
			<!-- Image layer - OPTIMIZED: Better image loading -->
			<div
				class="absolute h-full w-full"
				:style="{
					left: `${(imgX / viewboxW) * 100}%`,
					top: `${(imgY / viewboxH) * 100}%`,
					width: `${(imgW / viewboxW) * 100}%`,
					height: `${(imgH / viewboxH) * 100}%`
				}"
			>
				<slot name="image">
					<!-- Use regular img for AVIF files, NuxtImg for others -->
					<img
						v-if="isAvifFile"
						:src="image.url"
						:alt="image.alt || 'Image'"
						:width="image.width || viewboxW"
						:height="image.height || viewboxH"
						:loading="lazy ? 'lazy' : 'eager'"
						:fetchpriority="fetchpriority"
						class="h-full w-full"
						:class="{ 'rounded-full object-cover': rounded }"
						decoding="sync"
					>
					<NuxtImg
						v-else
						:src="image.url"
						:alt="image.alt || 'Image'"
						:width="image.width || viewboxW"
						:height="image.height || viewboxH"
						:sizes="sizes"
						:densities="image.densities"
						:fit="rounded ? 'cover' : undefined"
						format="auto"
						quality="85"
						:fetchpriority="fetchpriority"
						:loading="lazy ? 'lazy' : 'eager'"
						class="h-full w-full"
						:class="{ 'rounded-full object-cover': rounded }"
						decoding="sync"
					/>
				</slot>
			</div>

			<!-- SVG underlay - RESTORED: Keep original Motion animations -->
			<svg
				width="100%"
				height="100%"
				:viewBox="`0 0 ${viewboxW} ${viewboxH}`"
				xmlns="http://www.w3.org/2000/svg"
				class="absolute inset-0 overflow-visible pointer-events-none -z-1"
				preserveAspectRatio="xMidYMid meet"
			>
				<!-- decorative circles - RESTORED: Original Motion animations -->
				<template v-if="circles.length">
					<Motion
						v-for="(c, i) in circles"
						:key="`circle-${i}`"
						as-child
						:initial="{ scale: 0.8, opacity: 0 }"
						:while-in-view="{ scale: 1, opacity: 1 }"
						:transition="{ type: 'spring', bounce: 0.5, duration: 1.3, delay: 0.5 + i * 0.1 }"
						:in-view-options="{ once: true }"
					>
						<circle
							:cx="c.cx"
							:cy="c.cy"
							:r="c.r"
							:fill="c.fill"
						/>
					</Motion>
				</template>

				<!-- arcs - RESTORED: Original Motion animations -->
				<template v-if="typeof arcs !== 'boolean' && arcs.length">
					<Motion
						v-for="(arc, i) in arcs"
						:key="`arc-${i}`"
						as-child
						:initial="{ x: -20, opacity: 0 }"
						:while-in-view="{ opacity: 1, transform: 'translateX(0px)' }"
						:transition="{ duration: 0.5, delay: 0.5 + i * 0.1 }"
						:in-view-options="{ once: true }"
						class="will-change-transform"
					>
						<path
							:d="arc.d"
							:stroke="arc.stroke"
							stroke-miterlimit="10"
							fill="none"
						/>
					</Motion>
				</template>
			</svg>

			<!-- SVG overlay - RESTORED: Keep original Motion animations -->
			<svg
				width="100%"
				height="100%"
				:viewBox="`0 0 ${viewboxW} ${viewboxH}`"
				xmlns="http://www.w3.org/2000/svg"
				class="absolute inset-0 overflow-visible pointer-events-none z-10"
				preserveAspectRatio="xMidYMid meet"
			>
				<!-- decorative circles with icons - RESTORED: Original Motion animations -->
				<template v-if="icons.length">
					<g
						v-for="(icon, i) in icons"
						:key="`icon-group-${i}`"
					>
						<Motion
							as-child
							:initial="{ scale: 0.8, opacity: 0, rotate: i % 2 === 0 ? -5 : 5 }"
							:while-in-view="{ scale: 1, opacity: 1, rotate: 0 }"
							:transition="{ type: 'spring', bounce: 0.5, duration: 1.6, delay: 0.7 + i * 0.1 }"
							:in-view-options="{ once: true }"
						>
							<foreignObject
								:x="icon.cx - icon.r"
								:y="icon.cy - icon.r"
								:width="icon.r * 2"
								:height="icon.r * 2"
							>
								<div
									class="w-full h-full p-2 rounded-full flex items-center justify-center bg-default"
									:class="icon.borderClass || 'border-2 border-secondary'"
								>
									<UIcon
										:name="icon.iconName"
										:class="[
											icon.iconClass || 'text-primary',
											'w-full h-full'
										]"
									/>
								</div>
							</foreignObject>
						</Motion>
					</g>
				</template>
			</svg>
		</div>
	</div>
</template>

<script setup lang="ts">
import type {
	Arc,
	Circle,
	Icon,
	Image,
	Line,
	Viewbox
} from '@/types/components/svg-image'
import { Motion } from 'motion-v'

// OPTIMIZED: Pre-compute static values
const DEFAULT_CONFIG = {
	width: 405,
	height: 405,
	offset: 0,
	rotation: 0,
	length: 180,
	direction: 'clockwise',
	gap: 30,
	stroke: '#FF5733'
} as const

const props = defineProps({
	id: {
		type: String,
		required: true
	},
	fetchpriority: {
		type: String,
		default: 'auto'
	},
	lazy: {
		type: Boolean,
		default: false
	},
	rounded: {
		type: Boolean,
		default: false
	},
	viewbox: {
		type: Object as PropType<Viewbox>,
		default: () => ({
			width: 405,
			height: 405
		})
	},
	image: {
		type: Object as PropType<Image>,
		default: () => ({
			url: '/home-content-aside-1.avif',
			alt: 'Maverix Fiber-fast internet',
			offsetX: 0,
			offsetY: 0,
			height: 405,
			width: 405
		})
	},
	sizes: {
		type: String,
		default: '200px sm:360px lg:320px xl:417px'
	},
	lines: {
		type: Array as PropType<Line[]>,
		default: () => ([])
	},
	circles: {
		type: Array as PropType<Circle[]>,
		default: () => ([])
	},
	icons: {
		type: Array as PropType<Icon[]>,
		default: () => ([])
	}
})

// OPTIMIZED: Cache computed values
const isAvifFile = computed(() => props.image.url.toLowerCase().endsWith('.avif'))

// OPTIMIZED: Pre-calculate dimensions once
const dimensions = computed(() => {
	const viewboxH = props.viewbox?.height || DEFAULT_CONFIG.height
	const viewboxW = props.viewbox?.width || DEFAULT_CONFIG.width
	const imgX = props.image?.offsetX || DEFAULT_CONFIG.offset
	const imgY = props.image?.offsetY || DEFAULT_CONFIG.offset
	const imgH = props.image?.height ? props.image.height * 6 : viewboxH
	const imgW = props.image?.width ? props.image.width * 6 : viewboxW

	return { viewboxH,
		viewboxW,
		imgX,
		imgY,
		imgH,
		imgW }
})

// OPTIMIZED: Use cached dimensions
const {
	viewboxH, viewboxW, imgX, imgY, imgH, imgW
} = toRefs(dimensions.value)

// OPTIMIZED: Memoize expensive calculations
const arcs = computed(() => {
	if (!props.lines?.length) return false
	return props.lines.map((line: Line) => computeArc(line))
})

const {
	rounded,
	image,
	icons,
	circles
} = toRefs(props)

const viewboxCenterX = computed(() => viewboxW.value / 2)
const viewboxCenterY = computed(() => viewboxH.value / 2)

const deg2rad = (d: number) => (d * Math.PI) / 180

const computeArc = (line: Line): Arc => {
	const {
		rotation = DEFAULT_CONFIG.rotation,
		length = DEFAULT_CONFIG.length,
		direction = DEFAULT_CONFIG.direction,
		gap = DEFAULT_CONFIG.gap,
		stroke = DEFAULT_CONFIG.stroke
	} = line

	const r = Math.min(viewboxH.value, viewboxW.value) / 2 + gap
	const startRad = deg2rad(rotation)
	const sweepRad = deg2rad(length)
	const endRad = direction === 'clockwise'
		? startRad + sweepRad
		: startRad - sweepRad

	// Use viewbox center instead of image center for arc calculations
	const x1 = viewboxCenterX.value + r * Math.sin(startRad)
	const y1 = viewboxCenterY.value - r * Math.cos(startRad)
	const x2 = viewboxCenterX.value + r * Math.sin(endRad)
	const y2 = viewboxCenterY.value - r * Math.cos(endRad)

	const largeArcFlag = length > 180 ? 1 : 0
	const sweepFlag = direction === 'clockwise' ? 1 : 0

	return {
		d: `M${x1},${y1} A${r},${r} 0 ${largeArcFlag},${sweepFlag} ${x2},${y2}`,
		stroke: stroke
	}
}
</script>
